import { createApi } from "@reduxjs/toolkit/query/react";
import { axiosBaseQuery } from "@shared/lib/http/axiosBaseQuery";
import { stableSerialize } from "@shared/lib/http/stableSerialize";
import { ItemEntriesPaginatedSchema, SearchItemEntriesBrowseResponse } from "@models/item-entry";
import {
  ITEM_ENTRY_BROWSE_PER_PAGE,
  ItemEntrySearchParams,
  getItemEntrySearchBody,
} from "./itemEntriesBrowse.utils";

export const itemEntriesApi = createApi({
  reducerPath: "itemEntriesApi",
  baseQuery: axiosBaseQuery({ baseUrl: "/api/" }),
  tagTypes: ["itemEntriesBrowse"],
  endpoints: (b) => ({

    searchItemEntriesBrowse: b.query<SearchItemEntriesBrowseResponse, ItemEntrySearchParams>({
      query: searchItemEntriesBrowseQuery,
      serializeQueryArgs: searchItemEntriesBrowseSerializeQueryArgs,
      transformResponse: parseItemEntriesBrowseResponse,
      providesTags: searchItemEntriesBrowseProvidesTags,
    }),
    
  }),
});

export const {
  useSearchItemEntriesBrowseQuery,
} = itemEntriesApi;


/**
 * Validates a paginated item entries browse API payload.
 * @param response unknown - Raw response body from axiosBaseQuery.
 * @returns SearchItemEntriesBrowseResponse - Parsed paginated payload with rows and cursor meta.
 */
function parseItemEntriesBrowseResponse(response: unknown): SearchItemEntriesBrowseResponse {

  return ItemEntriesPaginatedSchema.parse(response);
}


/**
 * Builds a stable cache key for item entries browse queries with object args.
 * @param endpointName string - RTK Query endpoint name.
 * @param queryArgs ItemEntrySearchParams - Browse filters, sort, and pagination.
 * @returns string - Serialized cache key for the query args.
 */
function searchItemEntriesBrowseSerializeQueryArgs({ endpointName, queryArgs }: { endpointName: string; queryArgs: ItemEntrySearchParams }): string {
  const { useCache: _omit, ...cacheKeyArgs } = queryArgs;

  return `${endpointName}:${stableSerialize(cacheKeyArgs)}`;
}


/**
 * Builds the axios request for item entries browse search.
 * @param params ItemEntrySearchParams - Item id, filters, sort, and pagination.
 * @returns object - URL, method, and POST body for item-entries/search.
 */
function searchItemEntriesBrowseQuery({ itemId, filter, per_page, sort, useCache, ...rest }: ItemEntrySearchParams) {
  return {
    url: "item-entries/search",
    method: "POST" as const,
    data: getItemEntrySearchBody({
      itemId,
      filter: { item_id: itemId, ...filter },
      sort: sort ?? ["-id"],
      per_page: per_page ?? ITEM_ENTRY_BROWSE_PER_PAGE,
      useCache,
      ...rest,
    }),
  };
}


/**
 * Resolves RTK Query cache tags for item entries browse by item id.
 * @param _res SearchItemEntriesBrowseResponse | undefined - Successful query result (unused).
 * @param _err unknown - Query error (unused).
 * @param arg ItemEntrySearchParams - Browse args containing itemId.
 * @returns Array - Cache tag keyed by item id.
 */
function searchItemEntriesBrowseProvidesTags(
  _res: SearchItemEntriesBrowseResponse | undefined,
  _err: unknown,
  arg: ItemEntrySearchParams,
) {
  return [{ type: "itemEntriesBrowse" as const, id: arg.itemId }];
}
